import { useProspectsQuery } from 'hooks/useProspectsQuery';
import { useEffect, useState } from 'react';
import { apiProspectsRepository } from 'services/repositories/implementations/ApiProspectsRepository';
import type { AxiosClient } from '../../../../services/api/client.ts';
import { documentServiceClient } from '../../../../services/api/client.ts';
import type { ProspectResponseDtoTypeEnum } from '@proposify/document-service-api-schema';

export interface ProspectOption {
  label: string;
  value: string;
  id: string | number;
  type: ProspectResponseDtoTypeEnum;
}

const defaultPaginationValues = { offset: 0, limit: 50 };

export function useProspect({ searchQuery = '', enabled = true }: { searchQuery?: string; enabled?: boolean } = {}) {
  const [options, setOptions] = useState<ProspectOption[] | undefined>(undefined);
  const [pagination, setPagination] = useState(defaultPaginationValues);
  const apiDataClient: AxiosClient = documentServiceClient;
  const prospectsQueryResult = useProspectsQuery({
    offset: pagination.offset,
    limit: pagination.limit,
    search: searchQuery,
    apiDataClient: apiDataClient,
    enabled,
  });

  useEffect(() => {
    const opts: ProspectOption[] | undefined = prospectsQueryResult.data?.map((prospect) => {
      return {
        label: prospect.name,
        value: prospect.uid,
        id: prospect.id,
        type: prospect.type,
      };
    });

    if (!opts) {
      return;
    }

    setOptions((prevOptions) => {
      if (searchQuery.trim().length > 0) {
        return opts;
      }

      if (prevOptions) {
        const seen = new Set(prevOptions.map((option) => option.id));
        return prevOptions.concat(opts.filter((option) => !seen.has(option.id)));
      }
      return opts;
    });
  }, [prospectsQueryResult.data]);

  const onMenuScrollToBottom = async () => {
    if (!prospectsQueryResult.isLoading) {
      const newOffset = pagination.offset + pagination.limit;
      setPagination({ offset: newOffset, limit: pagination.limit });
    }
  };

  const loadOptions = async (
    inputValue: string,
    callback: (options: ProspectOption[], matchedProspect: ProspectOption | undefined) => void
  ) => {
    try {
      setPagination(defaultPaginationValues);
      const queryParams = {
        offset: defaultPaginationValues.offset,
        limit: defaultPaginationValues.limit,
        search: inputValue,
      };
      const response = await apiProspectsRepository.getProspects(queryParams, apiDataClient);

      const mappedOptions = response.map((prospect) => ({
        label: prospect.name,
        value: prospect.uid,
        id: prospect.id,
        type: prospect.type,
      }));

      const matchedProspect =
        inputValue.trim().length > 0
          ? mappedOptions.find((option) => option.label.localeCompare(inputValue, undefined, { sensitivity: 'accent' }) === 0)
          : undefined;

      callback(mappedOptions, matchedProspect);
      return { options: mappedOptions, matchedProspect };
    } catch {
      callback([], undefined);
      return { options: [], matchedProspect: undefined };
    }
  };

  return {
    onMenuScrollToBottom,
    isLoading: prospectsQueryResult.isLoading,
    options: options,
    loadOptions,
  };
}
